import { Component, inject, input, OnChanges, output, SimpleChanges, OnInit, ViewChild } from '@angular/core';
import {PRIORITY_VALUES, Publication} from "../../models/publication.model";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors
} from "@angular/forms";
import {
  RdsBadgeComponent,
  RdsButtonModule,
  RdsDividerModule,
  RdsEmptyStateModule,
  RdsFormFieldModule,
  RdsIconComponent,
  RdsPopoverModule,
  RdsProgressSpinnerComponent,
  RdsRadioButtonModule,
  RdsSwitchModule
} from "@rds/angular-components";

import { firstValueFrom } from 'rxjs';
import {FormValidity} from "../detail-view-wrapper.component";
import {ValidationErrorCollector} from "../../models/validation-error-collector.model";
import {DateFormatter} from "../../models/date-formatter.model";
import {ExpandableInputComponent} from "../expandable-input/expandable-input.component";
import { UserService } from '../../services/user.service';
import { User } from '../../models/user.model';
import { StrategicPillar, StrategicPillarService } from '../../services/strategic-pillar.service';
import { AudienceService, UserAudienceResponse, UnifiedAudience } from '../../services/audience.service';
import { PillarsAudiencesSectionComponent } from '../../shared/pillars-audiences-section/pillars-audiences-section.component';

export type VisibilityType = 'visible' | 'hidden'

type DetailedViewForm = FormGroup<{
  metadata: FormGroup<{
    shortTitle: FormControl<string | null>;
    title: FormControl<string>;
    documentNumber: FormControl<string | null>;
    authors: FormArray<FormControl<string | null>>;
    target: FormControl<string>;
    congress_acronym: FormControl<string | null>;
    studies: FormArray<FormControl<string | null>>;
    date: FormControl<string>;
    link: FormControl<string | null>;
  }>;
  priority: FormControl<3 | 2 | 1 | 0 | ((priorityControl: FormControl<any>) => ValidationErrors | null) | null>;
  priorityLinks: FormGroup<{
    dataTraining: FormControl<string | ((priorityLinkControl: FormControl<string>) => ValidationErrors | null) | null>;
    publicationAsSlides: FormControl<string | ((priorityLinkControl: FormControl<string>) => ValidationErrors | null) | null>;
    metisStatements: FormControl<string | ((priorityLinkControl: FormControl<string>) => ValidationErrors | null) | null>;
  }>;
  keyWords: FormArray<FormControl<string | null>>;
  notes: FormControl<string | null>;
  isVisible: FormControl<boolean | null>;
}>

@Component({
  selector: 'app-publication-detail-view',
  standalone: true,
  imports: [
    RdsButtonModule,
    RdsDividerModule,
    RdsFormFieldModule,
    RdsIconComponent,
    RdsRadioButtonModule,
    ReactiveFormsModule,
    RdsProgressSpinnerComponent,
    RdsEmptyStateModule,
    RdsPopoverModule,
    RdsSwitchModule,
    ExpandableInputComponent,
    PillarsAudiencesSectionComponent,
  ],
  templateUrl: './publication-detail-view.component.html',
  styleUrl: './publication-detail-view.component.scss'
})
export class PublicationDetailViewComponent implements OnChanges, OnInit {
  publication = input.required<Publication>()
  isLoadingChanges = input.required<boolean>()
  newPublication = output<{ data: Publication, valid: FormValidity, strategicPillarIds: number[], audiences: UnifiedAudience[] }>()
  private readonly formBuilder = inject(FormBuilder)
  form!: DetailedViewForm
  authorListExpanded = false
  editorActionsVisible: VisibilityType = 'hidden'
  isViewer: boolean = true
  pendingStrategicPillars: { pillarIds: number[], pillars: StrategicPillar[] } | null = null;
  pendingAudiences: { audienceIds: number[], audiences: UnifiedAudience[] } | null = null;
  @ViewChild(PillarsAudiencesSectionComponent) pillarsAudiencesSection!: PillarsAudiencesSectionComponent;

  constructor(private userService: UserService, private strategicPillarService: StrategicPillarService, private audienceService: AudienceService) {}

  get displayTarget(): string {
    const pub = this.publication();
    return pub.metadata.congress_acronym || pub.metadata.target;
  }

  get targetTooltip(): string {
    const pub = this.publication();
    return pub.metadata.congress_acronym ? pub.metadata.target : '';
  }

  get targetControl(): FormControl<string> {
    const pub = this.publication();
    return pub.metadata.congress_acronym 
      ? this.form.controls.metadata.controls.congress_acronym as FormControl<string>
      : this.form.controls.metadata.controls.target;
  }

  private setupForm() {
    const p = this.publication()
    const presentationDate = new DateFormatter(p.metadata.expectedPublicationDate, false).presentationDate

    this.form = this.formBuilder.group({
      metadata: this.formBuilder.group({
        shortTitle: this.formBuilder.control({value: p.metadata.shortTitle ?? null, disabled: true}),
        title: this.formBuilder.control({value: p.metadata.title, disabled: true}, {nonNullable: true}),
        documentNumber: this.formBuilder.control({value: p.metadata.documentNumber, disabled: true}),
        authors: this.formBuilder.array([...p.metadata.authors]),
        target: this.formBuilder.control({value: p.metadata.target, disabled: true}, {nonNullable: true}),
        congress_acronym: this.formBuilder.control({value: p.metadata.congress_acronym ?? null, disabled: true}),
        studies: this.formBuilder.array([...p.studyPresentations]),
        date: this.formBuilder.control({value: presentationDate,disabled: true}, {nonNullable: true}),
        link: this.formBuilder.control({value: p.metadata.link, disabled: true}),
      }),
      priority: [{value: p.priority, disabled: this.isViewer}, this.priorityValidator],
      priorityLinks: this.formBuilder.group({
        dataTraining: [{value: p.priorityLinks.dataTraining, disabled: this.isViewer}, this.generatePriorityLinkValidator("dataTraining")],
        publicationAsSlides: [{value: p.priorityLinks.publicationAsSlides, disabled: this.isViewer}, this.generatePriorityLinkValidator("publicationAsSlides")],
        metisStatements: [{value: p.priorityLinks.metisStatements, disabled: this.isViewer}, this.generatePriorityLinkValidator("metisStatements")],
      }),
      keyWords: this.formBuilder.array(p.keyWords.map(k => [{value: k, disabled: this.isViewer}, this.keywordValidator])),
      notes: [{value: p.notes, disabled: this.isViewer}],
      isVisible: [{value: p.isVisible == false ? p.isVisible : true, disabled: this.isViewer}],
    });
    this.form.controls.metadata.controls.authors.controls.forEach(authorControl => {
      authorControl.disable()
    })
    this.form.controls.metadata.controls.studies.controls.forEach(studyControl => {
      studyControl.disable()
    })
    this.form.get('priority')?.enable({ onlySelf: true, emitEvent: false });
    this.form.get('priority')?.valueChanges?.subscribe(_ => {
      const dT = this.form.controls.priorityLinks.controls.dataTraining
      const pS = this.form.controls.priorityLinks.controls.publicationAsSlides
      const mS = this.form.controls.priorityLinks.controls.metisStatements
      dT.markAsTouched()
      dT.updateValueAndValidity()
      pS.markAsTouched()
      pS.updateValueAndValidity()
      mS.markAsTouched()
      mS.updateValueAndValidity()
    })
  }

  priorityValidator(priorityControl: FormControl<number>) {
    const p = priorityControl.value
    if (!PRIORITY_VALUES.includes(p))
      return {invalidPriority: true} as ValidationErrors
    else
      return null
  }

  generatePriorityLinkValidator(fieldName: keyof typeof this.form.controls.priorityLinks.value) {
    const validator = (_: FormControl<string>): ValidationErrors | null => {
      if (this.priorityLinkIsRequired(fieldName) && this.priorityLinkIsEmpty(fieldName))
        return {emptyRequiredLink: true}

      return null
    }
    // Add this as binding to the validator as it will not be run from inside the context of this classes instance
    // Makes sure that each this keyword refers to this exact PublicationDetailViewComponent instance
    validator.bind(this)
    return validator
  }

  priorityLinkIsRequired(fieldName: keyof typeof this.form.controls.priorityLinks.value) {
    const p = this.form?.get("priority")?.value
    if (typeof p == 'number') {
      return Publication.priorityLinkIsRequired(fieldName, p)
    }
    return false
  }

  priorityLinkIsEmpty(fieldName: keyof typeof this.form.controls.priorityLinks.value) {
    return !this.form?.controls.priorityLinks.controls[fieldName].value
  }

  keywordValidator(keywordControl: AbstractControl<string>) {
    const keyword = keywordControl.value
    if (!keyword || typeof keyword !== 'string' || keyword.trim() == "")
      return {emptyKeywordError: true} as ValidationErrors
    else
      return null
  }

  addKeyword() {
    this.form.controls.keyWords.push(this.formBuilder.control("", this.keywordValidator))
  }

  removeKeyword(i: number) {
    this.form.controls.keyWords.removeAt(i)
  }

  clearEmptyKeywords(){
      const indices: [string | null, number][] = this.form.controls.keyWords.value.map((x, i) => [x, i] as [string | null, number]).filter(k => k[0] === "")
      for(let [offset, idx] of indices.entries()){
          this.form.controls.keyWords.removeAt(idx[1] - offset)
      }
  }

  onKeywordKeypress(e: KeyboardEvent){
      if(e.code == "Enter"){
          e.preventDefault()
      }
  }

  onSubmit() {
    // Close any open dropdowns before submitting
    this.pillarsAudiencesSection?.closeAllDropdowns();
    
    let errors = new ValidationErrorCollector(this.form).errors
    if(errors["emptyKeywordError"]){
      this.clearEmptyKeywords()
      errors = new ValidationErrorCollector(this.form).errors
    }

    const validity: FormValidity = this.getValidity(errors)
    const copy = Object.assign(new Publication(), JSON.parse(JSON.stringify(this.publication())))
    let post = Object.assign(copy, this.form.value)
    if(copy.metadata.studies && copy.metadata.studies.length == 0){  
      post.metadata = this.publication().metadata
    }

    const existingPillarIDs = (this.publication().strategicPillars || []).map(p => p.id);
    const existingAudiences = (this.publication() as any).audiences || [];

    // Always send current sets even if unchanged
    const payload = {
      data: post,
      valid: validity,
      strategicPillarIds: this.pendingStrategicPillars ? this.pendingStrategicPillars.pillarIds : existingPillarIDs,
      audiences: this.pendingAudiences ? this.pendingAudiences.audiences : existingAudiences
    };

    this.newPublication.emit(payload)
  }

  openLink(url: string) { 
    const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
    const newWindow = window.open(formattedUrl, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  }

  openMetisLink(url: string = typeof this.form.controls.priorityLinks.controls.metisStatements.value === 'string' ? this.form.controls.priorityLinks.controls.metisStatements.value : '') {
    this.openLink(url)
  }
  
  openTrainingLink(url: string = typeof this.form.controls.priorityLinks.controls.dataTraining.value === 'string' ? this.form.controls.priorityLinks.controls.dataTraining.value : '') {
    this.openLink(url)
  }

  openSlidesLink(url: string = typeof this.form.controls.priorityLinks.controls.publicationAsSlides.value === 'string' ? this.form.controls.priorityLinks.controls.publicationAsSlides.value : '') {
    this.openLink(url)
  }

  openPubCenterLink() {
    this.openLink(this.getPubCenterLink())
  }

  private getValidity(errors: ValidationErrors): FormValidity{
    return Object.keys(errors).length == 0? 'valid'
      : (errors['invalidPriority']? 'invalid_priority'
      : (errors['required'] ? 'required'
      : 'warning'))
  }

  getPubCenterLink(): string {
    const documentNumber = this.publication().metadata?.documentNumber;
    return `https://pubcenter.roche.com/pubcenter-ui/#/publication/${documentNumber}`
  }

  shouldShowPubCenterLink() {
    const status = this.publication().metadata.status as string
    return this.publication().metadata.documentNumber && (status == 'Accepted' || status == 'Presented' || status == 'Published')
  }

  // Strategic Pillar event handlers
  onPillarChanged(selection: { pillarIds: number[], pillars: StrategicPillar[] }) {
    // Store the pending change for when user clicks Save
    this.pendingStrategicPillars = selection;
    console.log('Strategic pillar selection changed:', selection);
  }

  onAudienceSelectionChange(selection: { audienceIds: number[], audiences: UnifiedAudience[] }) {
    // Store the pending change for when user clicks Save
    this.pendingAudiences = selection;
    console.log('Audience selection changed:', selection);
  }

  ngOnInit() {
    this.userService.getUserData().then((user: User) => {
      this.editorActionsVisible = user.isViewer ? 'hidden' : 'visible'
      this.isViewer = user.isViewer
      this.setupForm()
    }).catch((error: any) => {
      console.error('Error fetching user data:', error);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['publication']) {
      this.setupForm()
    }
  }
}
